import {Component, Inject, OnDestroy, Optional} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AuthControllerService, StaffProfileControllerService} from '../../@services/gen';
import {LocalstorageService} from '../../@services/localstorage.service';

@Component({
  selector: 'app-manager-auth-dialog',
  templateUrl: './manager-auth-dialog.component.html',
  styleUrls: ['./manager-auth-dialog.component.scss']
})
export class ManagerAuthDialogComponent implements OnDestroy {
  username = '';
  password = '';
  errorMessage = '';
  loading = false;
  showPassword = false;
  remark = '';
  requireRemark = false;

  // NFC
  nfcSupported = false;
  nfcAuthenticated = false;
  nfcStatus: 'scanning' | 'verifying' | 'success' | 'error' = 'scanning';
  nfcErrorMessage = '';
  nfcResult: any = null;
  private nfcListener: any;

  constructor(
    private dialogRef: MatDialogRef<ManagerAuthDialogComponent>,
    private authService: AuthControllerService,
    private staffProfileService: StaffProfileControllerService,
    private ls: LocalstorageService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.nfcSupported = data?.nfcEnabled ?? false;
    this.requireRemark = data?.requireRemark ?? false;

    if (this.nfcSupported) {
      this.startNfcListening();
    }
  }

  ngOnDestroy() {
    this.stopNfcListening();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  private startNfcListening() {
    this.nfcStatus = 'scanning';
    this.nfcListener = (event: MessageEvent) => {
      const data = event.data;
      if (data.type === 'NFC_RESULT') {
        this.verifyNfcCard(data.uid);
      } else if (data.type === 'NFC_ERROR') {
        this.nfcStatus = 'error';
        this.nfcErrorMessage = data.error || 'NFC scan failed';
      }
    };
    window.addEventListener('message', this.nfcListener);

    const reactNative = (window as any).ReactNativeWebView;
    if (reactNative) {
      reactNative.postMessage(JSON.stringify({ type: 'SCAN_NFC' }));
    }
  }

  private stopNfcListening() {
    if (this.nfcListener) {
      window.removeEventListener('message', this.nfcListener);
      this.nfcListener = null;
    }
  }

  private verifyNfcCard(uid: string) {
    this.nfcStatus = 'verifying';
    const merchant = this.ls.getMerchant();

    this.staffProfileService.getStaffByNfcCardIdPost({
      nfcCardId: uid,
      merchantId: merchant.id
    }).subscribe({
      next: (staff: any) => {
        if (!staff) {
          this.nfcStatus = 'error';
          this.nfcErrorMessage = 'No staff found for this NFC card.';
          return;
        }

        const role = staff.dposUserRole?.name?.toLowerCase();
        if (['manager', 'admin', 'super-admin'].includes(role)) {
          this.nfcStatus = 'success';
          this.nfcAuthenticated = true;
          this.nfcResult = { username: staff.name, role: staff.dposUserRole?.name };
          if (!this.requireRemark) {
            setTimeout(() => {
              this.dialogRef.close({
                success: true,
                username: staff.name,
                role: staff.dposUserRole?.name,
                remark: this.remark
              });
            }, 800);
          }
        } else {
          this.nfcStatus = 'error';
          this.nfcErrorMessage = 'Manager role required. This card belongs to a ' + (role || 'unknown role') + '.';
        }
      },
      error: () => {
        this.nfcStatus = 'error';
        this.nfcErrorMessage = 'Failed to verify NFC card. Please try again.';
      }
    });
  }

  retryNfc() {
    this.stopNfcListening();
    this.startNfcListening();
  }

  submit() {
    if (!this.username || !this.password) {
      this.errorMessage = 'Please enter username and password';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.authService.loginPost({
      username: this.username,
      password: this.password
    }).subscribe({
      next: (res: any) => {
        this.loading = false;
        const user = res.user;
        const role = user.role?.toLowerCase();

        if (['manager', 'admin', 'super-admin'].includes(role)) {
          this.dialogRef.close({
            success: true,
            username: user.username,
            role: user.role,
            remark: this.remark
          });
        } else {
          this.errorMessage = 'Insufficient permissions. Manager role required.';
        }
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = err?.error?.message || 'Invalid credentials';
      }
    });
  }

  submitNfcRemark() {
    if (this.nfcResult) {
      this.dialogRef.close({
        success: true,
        username: this.nfcResult.username,
        role: this.nfcResult.role,
        remark: this.remark
      });
    }
  }

  cancel() {
    this.dialogRef.close({ success: false });
  }
}
